import {
  Directive,
  ElementRef,
  AfterViewInit,
  ViewContainerRef,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  inject,
  input,
  output
} from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { JoyrideStepsContainerService } from '../services/joyride-steps-container.service';
import { JoyrideError, JoyrideStep } from '../joyride.domain';
import { JoyrideConstants } from '../joyride.constants';
import { JoyrideStepComponent } from '../components/step/joyride-step.component';
import { WINDOW_TOKEN } from '../../../services/window.token';

@Directive({
  selector: 'tdWbJoyrideStep, [tdWbJoyrideStep]'
})
export class JoyrideDirective implements AfterViewInit, OnChanges, OnDestroy {
  readonly name = input<string>(undefined, { alias: 'tdWbJoyrideStep' });
  readonly nextStep = input<string>(undefined);
  readonly title = input<string | Observable<string>>(undefined);
  readonly text = input<string | Observable<string>>(undefined);
  readonly customLabel = input<string | Observable<string>>(undefined);
  readonly iconSrc = input<string>(undefined);
  readonly stepPosition = input<string>(JoyrideConstants.NO_POSITION);
  readonly usedAsCoachMark = input<boolean>(false);
  readonly doNotTreatAsFixed = input<boolean>(false);
  // This is an override for the global option showPrevButton, if set to true, the prev button will be hidden for this step
  readonly doNotShowPrevButton = input<boolean>(false);
  readonly back = output<void>();
  readonly next = output<void>();
  readonly done = output<void>();

  private readonly browserWindow = inject(WINDOW_TOKEN);
  private readonly joyrideStepsContainer = inject(JoyrideStepsContainerService);
  private readonly viewContainerRef = inject(ViewContainerRef);

  private step: JoyrideStep;
  private readonly destroy: Subject<void> = new Subject<void>();

  constructor() {
    this.step = new JoyrideStep();
  }

  ngAfterViewInit(): void {
    this.step.position = this.stepPosition();
    this.step.targetViewContainer = this.viewContainerRef;
    this.setAsyncFields(this.step);
    this.step.nextClicked = this.next;
    this.step.backCliked = this.back;
    this.step.tourDone = this.done;
    const name = this.name();
    if (!name) {
      throw new JoyrideError('All the steps should have the "joyrideStep" property set with a custom name.');
    }
    this.step.name = name;
    this.step.iconSrc = this.iconSrc();
    this.step.transformCssStyle = this.browserWindow.getComputedStyle(this.viewContainerRef.element.nativeElement).transform;
    this.step.isElementOrAncestorFixed = this.doNotTreatAsFixed() ? false :
      this.isElementFixed(this.viewContainerRef.element) ||
      this.isAncestorsFixed(this.viewContainerRef.element.nativeElement.parentElement);
    this.step.usedAsCoachMark = this.usedAsCoachMark();
    this.step.doNotShowPrevButton = this.doNotShowPrevButton();
    this.step.stepComponent = JoyrideStepComponent;

    this.joyrideStepsContainer.addStep(this.step);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['title'] || changes['text'] || changes['customLabel']) {
      this.setAsyncFields(this.step);
    }
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  private isElementFixed(element: ElementRef): boolean {
    return this.browserWindow.getComputedStyle(element.nativeElement).position === 'fixed';
  }

  private setAsyncFields(step: JoyrideStep): void {
    const titleValue = this.title();
    if (titleValue instanceof Observable) {
      titleValue
        .pipe(takeUntil(this.destroy))
        .subscribe((title: string) => {
          step.title.next(title);
        });
    } else {
      step.title.next(titleValue);
    }
    const textValue = this.text();
    if (textValue instanceof Observable) {
      textValue
        .pipe(takeUntil(this.destroy))
        .subscribe((text: string) => {
          step.text.next(text);
        });
    } else {
      step.text.next(textValue);
    }
    const customLabel = this.customLabel();
    if (customLabel instanceof Observable) {
      customLabel
        .pipe(takeUntil(this.destroy))
        .subscribe((label: string) => {
          step.customLabel.next(label);
        });
    } else {
      step.customLabel.next(customLabel);
    }
  }

  private isAncestorsFixed(nativeElement: HTMLElement): boolean {
    if (!nativeElement || !nativeElement.parentElement) {
      return false;
    }
    const isElementFixed = this.browserWindow.getComputedStyle(nativeElement.parentElement).position === 'fixed';
    if (nativeElement.nodeName === 'BODY') {
      return isElementFixed;
    }
    if (isElementFixed) {
      return true;
    } else {
      return this.isAncestorsFixed(nativeElement.parentElement);
    }
  }
}
